import { useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query'
import { useTRPC } from './trpc'
import type { MeWallet } from './api-types'
import type { DepositItem, HistoryItem, TransferItem } from './activity'

/*
 * The wallet's whole history in big pages: the same lists the Transactions
 * table reads, so a report holds exactly what the table shows. Each page is
 * cached briefly on its own.
 */

type Page<T> = { items: T[]; nextCursor?: string | null }
type Trpc = ReturnType<typeof useTRPC>

const PAGE_STALE_MS = 15_000

/** every page of a list, newest first, 500 at a time */
async function allPages<T>(page: (cursor: string | null) => Promise<Page<T>>): Promise<T[]> {
  const items: T[] = []
  let cursor: string | null = null
  for (let i = 0; i < 400; i++) {
    const result = await page(cursor)
    items.push(...result.items)
    if (!result.nextCursor) break
    cursor = result.nextCursor
  }
  return items
}

// the router's own output types; a spread of the query options loses them for the type checker
function swapPages(trpc: Trpc, queryClient: QueryClient, walletIndex: number) {
  return allPages<HistoryItem>((cursor) => queryClient.fetchQuery({ ...trpc.swap.list.queryOptions({ walletIndex, cursor, limit: 500 }), staleTime: PAGE_STALE_MS }) as Promise<Page<HistoryItem>>)
}

/** Every deposit, swap, transfer and custody record of the wallet (the reports). */
export function useActivityHistory(wallet: MeWallet | null, exchange: boolean) {
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  return useQuery({
    queryKey: ['activity-history', wallet?.id ?? null, exchange],
    enabled: wallet !== null,
    staleTime: 30_000,
    queryFn: async () => {
      if (!wallet) throw new Error('No wallet.')
      const walletIndex = wallet.index
      const staleTime = PAGE_STALE_MS
      const [deposits, swaps, transfers, privateDeposits, privateWithdrawals, positions, vaultDeposits, vaultWithdrawals] = await Promise.all([
        exchange ? [] : allPages<DepositItem>((cursor) => queryClient.fetchQuery({ ...trpc.deposit.list.queryOptions({ walletIndex, cursor, limit: 500 }), staleTime }) as Promise<Page<DepositItem>>),
        swapPages(trpc, queryClient, walletIndex),
        exchange ? [] : allPages<TransferItem>((cursor) => queryClient.fetchQuery({ ...trpc.transfer.list.queryOptions({ walletIndex, cursor, limit: 500 }), staleTime }) as Promise<Page<TransferItem>>),
        exchange ? [] : queryClient.fetchQuery({ ...trpc.privateDeposit.list.queryOptions(), staleTime }),
        exchange ? [] : queryClient.fetchQuery({ ...trpc.privateWithdrawal.list.queryOptions(), staleTime }),
        queryClient.fetchQuery({ ...trpc.position.list.queryOptions(), staleTime }),
        queryClient.fetchQuery({ ...trpc.vault.deposits.queryOptions(), staleTime }),
        queryClient.fetchQuery({ ...trpc.vault.withdrawals.queryOptions(), staleTime }),
      ])
      return { deposits, swaps, transfers, privateDeposits, privateWithdrawals, positions, vaultDeposits, vaultWithdrawals }
    },
  })
}
